import { IViolation } from 'app/model/base/annotation';
import { AmazonS3LinkedService } from 'app/model/linkedservice/amazon-s3.linkedservice';
import { AzureBlobFSLinkedService } from 'app/model/linkedservice/azure-blob-fs.linkedservice';
import { AzureBlobStorageLinkedService } from 'app/model/linkedservice/azure-blob-storage.linkedservice';
import { GoogleCloudStorageLinkedService } from 'app/model/linkedservice/google-cloud-storage.linkedservice';
import { format } from 'app/shared/utils/string.util';

import { Constants } from 'app/shared/shared-constants';
import { DataFlowSink } from '../dataflow-sink';
import { DataFlowSource } from '../dataflow-source';
import { FileBasedSettings, FILEMODE } from './file-based-settings';
import { GlobalVariables } from 'app/services/globals';
import { LoggerService } from 'app/logger/logger.service';
import { IValidationContext, LakehouseLinkedService } from 'shell-models-lib';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';

export async function validateFilePathOfFileBasedSettings(settings: FileBasedSettings, isSink: boolean = false, context: IValidationContext): Promise<IViolation[]> {
    const violations: IViolation[] = [];
    if (!isSink) {
        const source = settings.owner as DataFlowSource;
        if (source.sourceLinkedService instanceof AmazonS3LinkedService || source.sourceLinkedService instanceof AzureBlobStorageLinkedService
            || source.sourceLinkedService instanceof AzureBlobFSLinkedService || source.sourceLinkedService instanceof GoogleCloudStorageLinkedService) {
            if (!settings.container.expression) {
                violations.push({
                    context: source,
                    message: () => stringResources.Dataflow_RequireContainer,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    redirectTo: { entity: source, attribute: { name: 'container' } as any }
                });
            } else if (GlobalVariables.containerValidation &&
                !Constants.slashSpaceValidation.test(settings.container.expression) &&
                settings.container.expression !== settings.container.templateExpression) {
                const errorMsg = format(stringResources.Dataset_FileName_SpaceSlashValidation, stringResources.FileSystem_DisplayText);
                const logger = await getServiceInstance<LoggerService>(context.contextData, 'LoggerService');
                logger.error('DF inline source validateFilePathOfFileBasedSettings', errorMsg);

                violations.push({
                    context: source,
                    message: () => errorMsg,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    redirectTo: { entity: source, attribute: { name: 'container' } as any }
                });
            }
        } else {
            if (settings.fileMode === FILEMODE.FILE) {
                // For Fabric Lakehouse Files, a file placed directly under the Files root is a valid
                // location, so the folder path is optional when a file name is provided. Only require a
                // folder path when no file name is given. Non-Lakehouse connectors still always require it.
                const isLakehouse = source.sourceLinkedService instanceof LakehouseLinkedService;
                const folderPathIsOptional = isLakehouse && !!settings.fileName.expression;
                if (!settings.folderPath.expression && !folderPathIsOptional) {
                    violations.push({
                        context: source,
                        message: () => stringResources.Dataflow_RequireFolderPath,
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        redirectTo: { entity: source, attribute: { name: 'folderPath' } as any }
                    });
                }
            }
        }
        if (settings.fileMode === FILEMODE.WILDCARD) {
            if (!source.wildcardPaths || source.wildcardPaths.length === 0 || source.wildcardPaths.filter((path) => !!path.expression).length === 0) {
                violations.push({
                    context: source,
                    message: () => stringResources.Dataflow_RequireWildcardPath,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    redirectTo: { entity: source, attribute: { name: 'wildcardPaths' } as any }
                });
            }
        }
    } else {
        const sink = settings.owner as DataFlowSink;
        if (sink.sinkLinkedService instanceof AmazonS3LinkedService || sink.sinkLinkedService instanceof AzureBlobStorageLinkedService
            || sink.sinkLinkedService instanceof AzureBlobFSLinkedService || sink.sinkLinkedService instanceof GoogleCloudStorageLinkedService) {
            if (!settings.container.expression) {
                violations.push({
                    context: sink,
                    message: () => stringResources.Dataflow_RequireContainer,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    redirectTo: { entity: sink, attribute: { name: 'container' } as any }
                });
            }
        } else {
            if (!settings.folderPath.expression) {
                violations.push({
                    context: sink,
                    message: () => stringResources.Dataflow_RequireFolderPath,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    redirectTo: { entity: sink, attribute: { name: 'folderPath' } as any }
                });
            }
        }
    }
    return violations;
}
